import React, { useEffect, useState } from "react";
import { Plus, Trash2, Check } from "lucide-react";
import { Spinner } from "./ui/kit";
import api, { apiError } from "../lib/api";

const POLE = "w-full min-w-0 rounded-[12px] bg-white border border-line-strong px-3 text-[16px] md:text-[14px] text-ink outline-none focus:border-ink disabled:bg-surface";
const radky = (text) => text.split("\n").map((x) => x.trim()).filter(Boolean);

function Sekce({ titul, popis, children, testid }) {
  return (
    <section className="pt-5 first:pt-0" data-testid={testid}>
      <h3 className="t-telo font-700 text-ink">{titul}</h3>
      {popis && <p className="t-meta text-muted mt-0.5 mb-2.5">{popis}</p>}
      {children}
    </section>
  );
}

/**
 * Nastavení → Šablona nabídky: texty PDF nabídky a souhrnu případu. Jednou
 * pro všechny nabídky; co nabídka zahrnuje / nezahrnuje se liší podle technologie.
 * Mění majitel, obchodník jen vidí.
 */
export default function SablonaNabidky({ muzeUpravit }) {
  const [s, setS] = useState(null);
  const [rozsah, setRozsah] = useState({});      // {kid: {zahrnuje: text, nezahrnuje: text}}
  const [busy, setBusy] = useState(false);
  const [ulozeno, setUlozeno] = useState(false);
  const [chyba, setChyba] = useState("");

  const prijmi = (data) => {
    setS(data);
    setRozsah(Object.fromEntries(Object.entries(data.technologie || {}).map(([kid, r]) =>
      [kid, { zahrnuje: (r.zahrnuje || []).join("\n"), nezahrnuje: (r.nezahrnuje || []).join("\n") }])));
  };
  useEffect(() => {
    api.get("/api/company/sablona-nabidky").then(({ data }) => prijmi(data))
      .catch((e) => setChyba(apiError(e.response?.data?.detail) || e.message));
  }, []);

  const zmen = (patch) => { setS((x) => ({ ...x, ...patch })); setUlozeno(false); };
  const zmenPolozku = (klic, i, patch) => zmen({ [klic]: s[klic].map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const ulozit = async () => {
    setBusy(true); setChyba("");
    try {
      const { data } = await api.put("/api/company/sablona-nabidky", {
        uvod: s.uvod, poznamka: s.poznamka,
        kroky: s.kroky.filter((k) => k.nazev.trim()),
        vyhody: s.vyhody.filter((v) => v.nazev.trim()),
        technologie: Object.fromEntries(Object.entries(rozsah).map(([kid, r]) =>
          [kid, { zahrnuje: radky(r.zahrnuje), nezahrnuje: radky(r.nezahrnuje) }])),
      });
      prijmi(data); setUlozeno(true);
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="card p-8 flex justify-center">{chyba ? <p className="t-meta text-danger">{chyba}</p> : <Spinner />}</div>;
  const dis = !muzeUpravit;
  return (
    <div className="card min-w-0" data-testid="nastaveni-sablona">
      <div className="px-4 md:px-5 pt-4 md:pt-[18px]">
        <h2 className="t-sekce font-700 text-ink">Šablona nabídky</h2>
        <p className="t-meta text-muted mt-0.5">Texty v PDF nabídky a souhrnu případu. Barvu technologie nastavíte v záložce Technologie.</p>
      </div>
      <div className="px-4 md:px-5 pt-4 pb-4 md:pb-[18px] flex flex-col divide-y divide-line-soft [&>*]:pb-5 [&>*:last-child]:pb-0">
        <Sekce titul="Úvod na titulní straně" popis="Začíná slovy „Dobrý den,“." testid="sablona-uvod">
          <textarea value={s.uvod} disabled={dis} maxLength={600} rows={3} onChange={(e) => zmen({ uvod: e.target.value })}
            className={`${POLE} py-2.5 resize-y`} data-testid="sablona-uvod-text" />
        </Sekce>

        <Sekce titul="Jak to bude dál" popis="Až 6 kroků na poslední straně nabídky." testid="sablona-kroky">
          <div className="flex flex-col gap-2">
            {s.kroky.map((k, i) => (
              <div key={i} className="grid grid-cols-[28px_minmax(0,1fr)_auto] md:grid-cols-[28px_minmax(0,1.1fr)_150px_minmax(0,2fr)_auto] gap-2 items-start" data-testid={`sablona-krok-${i}`}>
                <span className="h-7 w-7 mt-2 rounded-full bg-ink text-white text-[12px] font-700 inline-flex items-center justify-center">{i + 1}</span>
                <input value={k.nazev} disabled={dis} maxLength={60} placeholder="Název kroku" onChange={(e) => zmenPolozku("kroky", i, { nazev: e.target.value })} className={`${POLE} h-11 md:h-10`} />
                <input value={k.kdy} disabled={dis} maxLength={40} placeholder="Kdy (do 14 dnů)" onChange={(e) => zmenPolozku("kroky", i, { kdy: e.target.value })}
                  className={`${POLE} h-11 md:h-10 col-start-2 md:col-start-auto`} />
                <input value={k.text} disabled={dis} maxLength={240} placeholder="Co se v kroku stane" onChange={(e) => zmenPolozku("kroky", i, { text: e.target.value })}
                  className={`${POLE} h-11 md:h-10 col-start-2 md:col-start-auto`} />
                {muzeUpravit && (
                  <button type="button" onClick={() => zmen({ kroky: s.kroky.filter((_, j) => j !== i) })} aria-label={`Odebrat krok ${i + 1}`}
                    className="tap md:min-h-0 h-10 w-10 rounded-full inline-flex items-center justify-center text-muted hover:text-danger row-start-1 col-start-3 md:row-start-auto md:col-start-auto">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
            {muzeUpravit && s.kroky.length < 6 && (
              <button type="button" onClick={() => zmen({ kroky: [...s.kroky, { nazev: "", kdy: "", text: "" }] })}
                className="tap md:min-h-0 self-start inline-flex items-center gap-1 t-meta font-700 text-accent" data-testid="sablona-krok-pridat">
                <Plus size={15} strokeWidth={2.4} /> Přidat krok
              </button>
            )}
          </div>
        </Sekce>

        <Sekce titul="Proč s námi" popis="Až 6 krátkých důvodů pod kontaktem na poradce." testid="sablona-vyhody">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {s.vyhody.map((v, i) => (
              <div key={i} className="flex gap-2 items-start rounded-[14px] bg-surface p-2.5" data-testid={`sablona-vyhoda-${i}`}>
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                  <input value={v.nazev} disabled={dis} maxLength={40} placeholder="Návrh na míru" onChange={(e) => zmenPolozku("vyhody", i, { nazev: e.target.value })} className={`${POLE} h-10 font-600`} />
                  <input value={v.text} disabled={dis} maxLength={140} placeholder="Jedna věta" onChange={(e) => zmenPolozku("vyhody", i, { text: e.target.value })} className={`${POLE} h-10`} />
                </div>
                {muzeUpravit && (
                  <button type="button" onClick={() => zmen({ vyhody: s.vyhody.filter((_, j) => j !== i) })} aria-label="Odebrat"
                    className="tap md:min-h-0 h-10 w-10 rounded-full inline-flex items-center justify-center text-muted hover:text-danger shrink-0"><Trash2 size={15} /></button>
                )}
              </div>
            ))}
          </div>
          {muzeUpravit && s.vyhody.length < 6 && (
            <button type="button" onClick={() => zmen({ vyhody: [...s.vyhody, { nazev: "", text: "" }] })}
              className="tap md:min-h-0 mt-2 inline-flex items-center gap-1 t-meta font-700 text-accent"><Plus size={15} strokeWidth={2.4} /> Přidat důvod</button>
          )}
        </Sekce>

        <Sekce titul="Co nabídka zahrnuje" popis="Pod rozpočtem každé technologie. Každý řádek = jedna odrážka." testid="sablona-rozsah">
          <div className="flex flex-col gap-3">
            {s.kategorie.map((k) => (
              <div key={k.id} className="rounded-[14px] border border-line-soft p-3" data-testid={`sablona-rozsah-${k.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-3 w-3 rounded-full shrink-0" style={{ background: k.barva }} />
                  <span className="t-telo font-700 text-ink">{k.name}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[["zahrnuje", "Zahrnuje"], ["nezahrnuje", "Nezahrnuje"]].map(([klic, popisek]) => (
                    <label key={klic} className="min-w-0">
                      <span className="label-up text-muted block mb-1">{popisek}</span>
                      <textarea value={rozsah[k.id]?.[klic] || ""} disabled={dis} rows={4}
                        onChange={(e) => { setRozsah((r) => ({ ...r, [k.id]: { ...r[k.id], [klic]: e.target.value } })); setUlozeno(false); }}
                        className={`${POLE} py-2 resize-y`} data-testid={`sablona-${klic}-${k.id}`} />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Sekce>

        <Sekce titul="Poznámka pod rozpočtem" popis="Např. podmínky snížené sazby DPH nebo dopravy." testid="sablona-poznamka">
          <textarea value={s.poznamka} disabled={dis} maxLength={600} rows={3} onChange={(e) => zmen({ poznamka: e.target.value })}
            className={`${POLE} py-2.5 resize-y`} data-testid="sablona-poznamka-text" />
        </Sekce>

        {muzeUpravit && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-2 pt-5">
            {chyba && <p className="t-meta text-danger md:mr-auto" data-testid="sablona-chyba">{chyba}</p>}
            {ulozeno && <span className="t-meta text-success inline-flex items-center gap-1 md:mr-2" data-testid="sablona-ulozeno"><Check size={14} /> Uloženo</span>}
            <button type="button" onClick={ulozit} disabled={busy} data-testid="sablona-ulozit"
              className="tap md:min-h-0 w-full md:w-auto h-11 md:h-10 px-5 rounded-full bg-accent text-white text-[14px] md:text-[13px] font-700 inline-flex items-center justify-center disabled:opacity-60">
              {busy ? <Spinner /> : "Uložit šablonu"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
